// src/components/admin/ImageManager.js
import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { FiUpload, FiImage } from 'react-icons/fi';
import { useApiAuth } from '../../hooks/useApiAuth';
import { useSiteConfig } from '../../hooks/useSiteConfig';

function ImageManager() {
  const { config, refresh } = useSiteConfig();
  const { authFetch } = useApiAuth();
  const { getToken } = useAuth();
  const [logoPreview, setLogoPreview] = useState('');
  const [bannerPreview, setBannerPreview] = useState('');
  const [uploading, setUploading] = useState('');

  useEffect(() => {
    setLogoPreview(config.logoUrl || '/logo.png');
    setBannerPreview(config.bannerImageUrl || '');
  }, [config]);

  async function uploadImage(file, type) {
    setUploading(type);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const ext = file.name.split('.').pop();
      const filename = `${type}-${Date.now()}.${ext}`;
      const token = await getToken();

      const res = await fetch(`/api/admin/upload?filename=${encodeURIComponent(filename)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: arrayBuffer,
      });

      if (!res.ok) throw new Error('Upload failed');
      const { url } = await res.json();

      const configUpdate = type === 'logo' ? { logoUrl: url } : { bannerImageUrl: url };
      await authFetch('/api/admin/config', { method: 'PUT', body: configUpdate });
      await refresh();
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading('');
    }
  }

  function handleFileSelect(type) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        alert('File must be under 10MB');
        return;
      }
      const localUrl = URL.createObjectURL(file);
      if (type === 'logo') setLogoPreview(localUrl);
      else setBannerPreview(localUrl);
      uploadImage(file, type);
    };
    input.click();
  }

  return (
    <section className="admin-section">
      <h2 className="admin-section-title">Site Images</h2>
      <div className="admin-images-grid">
        <div className="admin-image-card">
          <h3 className="admin-image-label"><FiImage size={14} /> Logo</h3>
          <div className="admin-image-preview">
            {logoPreview && <img src={logoPreview} alt="Logo preview" />}
          </div>
          <button className="admin-upload-btn" onClick={() => handleFileSelect('logo')} disabled={uploading === 'logo'}>
            <FiUpload size={14} />
            {uploading === 'logo' ? 'Uploading…' : 'Upload Logo'}
          </button>
        </div>
        <div className="admin-image-card">
          <h3 className="admin-image-label"><FiImage size={14} /> Banner Background</h3>
          <div className="admin-image-preview admin-image-preview--wide">
            {bannerPreview && <img src={bannerPreview} alt="Banner preview" />}
            {!bannerPreview && <span className="admin-image-placeholder">No banner set</span>}
          </div>
          <button className="admin-upload-btn" onClick={() => handleFileSelect('banner')} disabled={uploading === 'banner'}>
            <FiUpload size={14} />
            {uploading === 'banner' ? 'Uploading…' : 'Upload Banner'}
          </button>
        </div>
      </div>
    </section>
  );
}

export default ImageManager;
